import type { Epic } from '../../index.ts'
import type { Action } from '@reduxjs/toolkit'
import { isAnyOf } from '@reduxjs/toolkit'
import { visualizationSlice } from '../../Slice/Visualization/VisualizationSlice.ts'
import { getCubeTable } from '../../../Repository/Visualization/CubeTableRegistry.ts'
import { repairSelectedPath } from '../../../View/Component/ConfigurableVisualization/Sinks/Selector/CubeFiltering.ts'
import { filter, mergeMap, of, withLatestFrom } from 'rxjs'

/**
 * Repairs a source's taxa-zoom path when the cube underneath it changes.
 *
 * Switching blast source (or the SPIN algorithm/point-in-time) swaps the taxonomy tree: different
 * sources fill different ranks, so a `selectedPath` captured under the old tree can prefix-match
 * nothing under the new one and the view silently empties. Rather than blank the path on every
 * switch (which throws away the user's place and defeats cross-source comparison), we re-anchor it
 * to the deepest surviving taxon via {@link repairSelectedPath}, falling back to root only when
 * nothing survives.
 *
 * Triggers on BOTH:
 *  - `updateSourceShared` — the knob change itself. Covers switching **back** to an already-loaded
 *    blast type, which does NOT re-fetch (no `cubeReady`), so this is the only signal there. The
 *    target cube is resident, so we repair synchronously.
 *  - `cubeReady` — the cold case: switching to a not-yet-loaded cube, repaired once its rows exist.
 *
 * `repairSelectedPath` returns the same array reference when the path still matches, so unrelated
 * knob changes and ordinary re-fetches are a cheap no-op that emit nothing. `setTaxaZoom` is FE-only
 * and not a cube trigger, so there is no feedback loop.
 */
export const taxaPathRepairEpic: Epic = (action$, state$, { [visualizationSlice.name]: visualization }) => {
  const triggers = isAnyOf(visualization.cubeReady, visualization.updateSourceShared)

  return action$.pipe(
    filter((a: Action) => triggers(a)),
    withLatestFrom(state$),
    mergeMap(([, s]) => {
      const scopeId = s.visualization.scope?.id
      if (!scopeId) return of()

      const out: Action[] = []
      for (const [sourceId, src] of Object.entries(s.visualization.sources)) {
        const path = src?.shared?.selectedPath
        if (!path || path.length === 0) continue
        const blastType = src?.shared?.blastType
        if (!blastType) continue
        const spin = src?.shared?.spin

        // Repair against the cube the source currently points at. If it isn't resident yet (a cold
        // switch), skip — the subsequent `cubeReady` for that key will re-run this and repair then.
        const table = getCubeTable(scopeId, blastType, spin)
        if (!table) continue

        const repaired = repairSelectedPath(table, path)
        // Same reference ⇒ still valid ⇒ nothing to do (the common case).
        if (repaired !== path) out.push(visualization.setTaxaZoom({ sourceId, path: repaired, auto: true }))
      }
      return of(...out)
    })
  )
}
